import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import './css/login.css';

const MIN_PASSWORD_LENGTH = 12; // keep in sync with LoginController.cs

// Password box with an eye button that shows or hides what was typed
const PasswordInput = ({ id, value, onChange, autoComplete, minLength }) => {
    const [visible, setVisible] = useState(false);

    return (
        <div className="login-password">
            <input
                id={id}
                className="login-input"
                type={visible ? 'text' : 'password'}
                value={value}
                onChange={onChange}
                required
                minLength={minLength}
                autoComplete={autoComplete}
            />
            <button
                type="button"
                className="login-password__toggle"
                onClick={() => setVisible(v => !v)}
                aria-label={visible ? 'Hide password' : 'Show password'}
                aria-pressed={visible}
                title={visible ? 'Hide password' : 'Show password'}
            >
                <FontAwesomeIcon icon={visible ? faEyeSlash : faEye} />
            </button>
        </div>
    );
};

const LoginPage = ({ setIsAuthenticated }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const navigate = useNavigate();

    // "login" or "change": which form is showing
    const [mode, setMode] = useState('login');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const switchMode = (nextMode) => {
        setMode(nextMode);
        setError('');
        setMessage('');
        setPassword('');
        setNewPassword('');
        setConfirmPassword('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');

        try {
            const response = await fetch('/api/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                setError(errorText || 'Login failed');
            } else {
                const data = await response.text();
                setMessage(data);

                // The server has set an HttpOnly login cookie; this flag only drives the UI
                setIsAuthenticated(true);

                // Navigate to blog update form for creating a new post
                navigate('/admin');
            }
        } catch (err) {
            setError('Network error: ' + err.message);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');

        if (newPassword.length < MIN_PASSWORD_LENGTH) {
            setError(`New password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('New passwords do not match.');
            return;
        }

        try {
            const response = await fetch('/api/login/change-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, currentPassword: password, newPassword }),
            });

            const text = await response.text();
            if (response.status === 429) {
                setError('Too many attempts. Wait a minute and try again.');
            } else if (!response.ok) {
                setError(text || 'Password change failed');
            } else {
                // Back to the login form, username kept, ready for the new password
                switchMode('login');
                setMessage(text);
            }
        } catch (err) {
            setError('Network error: ' + err.message);
        }
    };

    const isLogin = mode === 'login';

    return (
        <div className="login-card">
            <div className="login-card__body">
                <h2>{isLogin ? 'Login' : 'Change Password'}</h2>
                {error && <div className="login-alert login-alert--error" role="alert">{error}</div>}
                {message && <div className="login-alert login-alert--success" role="status">{message}</div>}

                <form onSubmit={isLogin ? handleSubmit : handleChangePassword}>
                    <div className="login-field">
                        <label htmlFor="login-username">Username</label>
                        <input
                            id="login-username"
                            className="login-input"
                            type="text"
                            value={username}
                            onChange={e => setUsername(e.target.value)}
                            required
                            autoComplete="username"
                        />
                    </div>

                    <div className="login-field">
                        <label htmlFor="login-password">{isLogin ? 'Password' : 'Current password'}</label>
                        <PasswordInput
                            id="login-password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            autoComplete="current-password"
                        />
                    </div>

                    {!isLogin && (
                        <>
                            <div className="login-field">
                                <label htmlFor="login-new-password">
                                    New password <span className="login-field__hint">(at least {MIN_PASSWORD_LENGTH} characters)</span>
                                </label>
                                <PasswordInput
                                    id="login-new-password"
                                    value={newPassword}
                                    onChange={e => setNewPassword(e.target.value)}
                                    minLength={MIN_PASSWORD_LENGTH}
                                    autoComplete="new-password"
                                />
                            </div>

                            <div className="login-field">
                                <label htmlFor="login-confirm-password">Confirm new password</label>
                                <PasswordInput
                                    id="login-confirm-password"
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                    minLength={MIN_PASSWORD_LENGTH}
                                    autoComplete="new-password"
                                />
                            </div>
                        </>
                    )}

                    <button type="submit" className="login-submit">
                        {isLogin ? 'Login' : 'Update Password'}
                    </button>
                </form>

                <button
                    type="button"
                    className="login-switch"
                    onClick={() => switchMode(isLogin ? 'change' : 'login')}
                >
                    {isLogin ? 'Change password' : 'Back to login'}
                </button>
            </div>
        </div>
    );
};

export default LoginPage;
